'use client';

import { useRouter, useSearchParams } from 'next/navigation';
import type { ReactElement } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/react/shallow';
import { CancelBonusModal } from '@/components/modals/CancelBonusModal';
import { TabNavigation } from '@/components/TabNavigation';
import { removeQueryParameter, removeUrlParameters } from '@/helpers/common';
import { Badge } from '@/modules/common/components/Badge';
import { useMarkBonusesSeenWhen } from '@/modules/promotions/bonusNotification';
import { useGetCommonStore } from '@/store';
import { ScContainer } from '@/styles/common';
import { BONUS_REF_PARAM } from './helpers';
import { ActiveBonuses } from './sections/ActiveBonuses';
import { useActiveBonuses } from './sections/ActiveBonuses/hooks/useActiveBonuses';
import { AllOffers } from './sections/AllOffers';
import { BonusHistory } from './sections/BonusHistory';
import { ScPromotionsPage } from './styled';

export enum PromotionPageTabs {
  ACTIVE_BONUSES = 'active-bonuses',
  BONUS_HISTORY = 'bonus-history',
  CASINO = 'casino',
}

export interface Tab {
  title: string | ReactElement;
  component: JSX.Element;
  id: PromotionPageTabs;
  isHidden?: boolean;
}

export const PromotionsPage = () => {
  const { t } = useTranslation('bonuses');
  const useCommonStore = useGetCommonStore();
  const activeBonuses = useActiveBonuses();
  const activeBonusesCount = activeBonuses.length;

  const {
    isMobileDevice,
    isAuthenticated,
    pendingUser,
    fetchBonuses,
    currentCancelBonusData,
  } = useCommonStore(
    useShallow((state) => ({
      isMobileDevice: state.isMobileDevice,
      isAuthenticated: state.isAuthenticated,
      pendingUser: state.pendingUser,
      fetchBonuses: state.fetchBonuses,
      currentCancelBonusData: state.currentCancelBonusData,
    }))
  );

  const [currentTabIndex, setCurrentTabIndex] = useState(0);

  const searchParams = useSearchParams();

  const { push } = useRouter();

  const switchToActiveBonuses = useCallback(() => {
    setCurrentTabIndex(1);
    removeUrlParameters(['tab', 'block']);
  }, []);

  const tabs: Tab[] = useMemo(
    () => [
      {
        title: t('tabs.offers'),
        component: (
          <AllOffers onPromocodeActivationSuccess={switchToActiveBonuses} />
        ),
        id: PromotionPageTabs.CASINO,
      },
      {
        title: (
          <>
            {t('tabs.myBonuses')}{' '}
            {!!activeBonusesCount && <Badge>{activeBonusesCount}</Badge>}
          </>
        ),
        component: <ActiveBonuses />,
        id: PromotionPageTabs.ACTIVE_BONUSES,
      },
      {
        title: t('tabs.bonusHistory'),
        component: <BonusHistory />,
        id: PromotionPageTabs.BONUS_HISTORY,
      },
    ],
    [t, activeBonusesCount, switchToActiveBonuses]
  );

  const currentTabComponent = useMemo(
    () => tabs[currentTabIndex].component,
    [currentTabIndex, tabs]
  );

  useMarkBonusesSeenWhen(
    tabs[currentTabIndex]?.id === PromotionPageTabs.ACTIVE_BONUSES
  );

  const tabNavigationProps = useMemo(
    () =>
      isMobileDevice
        ? {
            fullScreen: true,
          }
        : {
            maxWidth100: true,
            withoutShadow: true,
            menuItemWidth: `${100 / tabs.length}%`,
          },
    [isMobileDevice, tabs.length]
  );

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const tab = urlParams.get('tab');
    const tabIndex = tabs.findIndex(({ id }) => id.toString() === tab);

    if (tabIndex >= 0) {
      setCurrentTabIndex(tabIndex);
    }
  }, [searchParams, tabs]);

  const handleSelectTab = useCallback(
    (index: number) => {
      setCurrentTabIndex(index);

      removeQueryParameter(['tab', 'block'], push);
    },
    [push]
  );

  useEffect(() => {
    if (!isAuthenticated) return;

    fetchBonuses();
  }, [fetchBonuses, isAuthenticated]);

  useEffect(() => {
    const bonusRef = searchParams.get(BONUS_REF_PARAM);
    if (bonusRef) return;

    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentTabIndex, searchParams]);

  useEffect(() => {
    if (!(isAuthenticated || pendingUser)) {
      setCurrentTabIndex(0);
    }
  }, [isAuthenticated, pendingUser]);

  return (
    <>
      {currentCancelBonusData && (
        <CancelBonusModal {...currentCancelBonusData} />
      )}
      <ScContainer>
        <ScPromotionsPage>
          {isAuthenticated && (
            <TabNavigation
              navItems={tabs.map(({ title }) => title)}
              selectedItem={currentTabIndex}
              setSelectedItem={handleSelectTab}
              withoutShadow
              {...tabNavigationProps}
            />
          )}
          {currentTabComponent}
        </ScPromotionsPage>
      </ScContainer>
    </>
  );
};
